import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { getHeaders } from "../../utils/apiUtils";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  TableSortLabel,
  TablePagination,
  Alert,
  CircularProgress
} from "@mui/material";
import { 
  Restore as RestoreIcon, 
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon
} from "@mui/icons-material";
import api from "../../services/api";
import AcessoRestrito from '../../components/AcessoRestrito';
import { useSystemMessagesContext } from "../../contexts/SystemMessagesContext";

export default function RecuperarUsuario() {
  const { addError, addSuccess } = useSystemMessagesContext();
  const [usuariosExcluidos, setUsuariosExcluidos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    st_nome: "",
    st_email: "",
    en_perfil: "",
    razao_social: "",
  });
  const [orderBy, setOrderBy] = useState("st_nome");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openView, setOpenView] = useState(false);
  const [usuarioVisualizar, setUsuarioVisualizar] = useState(null);
  const [error, setError] = useState(null);

  // Referência para controle de montagem
  const mounted = useRef(true);

  const usuarioLogado = JSON.parse(localStorage.getItem("usuario") || "{}");
  const perfilLogado = usuarioLogado?.perfil || usuarioLogado?.en_perfil;
  const st_codHashEmpresa = usuarioLogado?.st_codHashEmpresa || localStorage.getItem("st_codHashEmpresa");

  // Debug: verificar dados do usuário logado
  console.log('Usuário logado:', usuarioLogado);
  console.log('Perfil logado:', perfilLogado);
  console.log('Hash empresa:', st_codHashEmpresa);

  // Função para criar logs de auditoria (REGRA 10)
  // Função de log de auditoria removida - agora é feita no backend

  // Carregar empresas
  const carregarEmpresas = useCallback(async () => {
    try {
      const headers = getHeaders();
      const response = await api.get('/empresas', { headers });
      
      if (response.data && Array.isArray(response.data)) {
        const lista = response.data;
        if (mounted.current) {
          setEmpresas(lista);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      addError('Erro ao carregar empresas');
    }
  }, [addError]);

  // Carregar usuários excluídos
  const carregarUsuariosExcluidos = useCallback(async () => {
    if (!mounted.current) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const headers = getHeaders();
      const res = await api.get('/usuarios/inativos', { headers });
      
      let lista = res.data?.dados || res.data || [];

      console.log('Usuários carregados da API:', lista.length);
      console.log('Dados dos usuários:', lista.map(u => ({
        id: u.id_usuario,
        nome: u.st_nome,
        email: u.st_email,
        bo_ativo: u.bo_ativo,
        st_status: u.st_status,
        ts_deleted: u.ts_deleted
      })));
      
      // Log detalhado para debug
      console.log('=== DEBUG DETALHADO DOS USUÁRIOS ===');
      lista.forEach((u, index) => {
        console.log(`Usuário ${index + 1}:`, {
          id: u.id_usuario,
          nome: u.st_nome,
          bo_ativo: u.bo_ativo,
          st_status: u.st_status,
          ts_deleted: u.ts_deleted,
          tipo_bo_ativo: typeof u.bo_ativo,
          valor_bo_ativo: u.bo_ativo
        });
      });

      // Filtrar apenas usuários inativos (bo_ativo = false/0 ou st_status = 'Inativo')
      lista = lista.filter((u) => {
        const isInactive = 
          u.bo_ativo === false || 
          u.bo_ativo === 0 || 
          u.bo_ativo === '0' ||
          u.st_status === 'Inativo' ||
          u.ts_deleted || // Se tem timestamp de exclusão
          (u.bo_ativo !== undefined && u.bo_ativo !== null && u.bo_ativo !== true && u.bo_ativo !== 1 && u.bo_ativo !== '1');
        
        console.log(`Filtro usuário ${u.id_usuario} (${u.st_nome}): bo_ativo=${u.bo_ativo} (${typeof u.bo_ativo}), st_status=${u.st_status}, ts_deleted=${u.ts_deleted}, isInactive=${isInactive}`);
        return isInactive;
      });

      console.log('Usuários inativos após filtro:', lista.length);

      // Adicionar nome da empresa
      lista = lista.map((u) => ({
        ...u,
        razao_social:
          empresas.find((e) => e.id_empresa === u.id_empresa)?.st_nome || "",
      }));

      // Filtrar por empresa se não for superusuário
      if (perfilLogado !== 'superusuario') {
        console.log('Aplicando filtro por empresa (não é superusuário)');
        console.log('Hash empresa do usuário logado:', st_codHashEmpresa);
        console.log('Usuários antes do filtro por empresa:', lista.length);
        
        lista = lista.filter((u) => {
          // Tentar ambos os campos possíveis
          const match = u.st_codHashEmpresa === st_codHashEmpresa || u.ts_codHashEmpresa === st_codHashEmpresa;
          console.log(`Usuário ${u.id_usuario} (${u.st_nome}): st_codHashEmpresa=${u.st_codHashEmpresa}, ts_codHashEmpresa=${u.ts_codHashEmpresa}, match=${match}`);
          return match;
        });
      } else {
        console.log('Usuário é superusuário - não aplicando filtro por empresa');
      }

      console.log('Usuários após filtro por empresa:', lista.length);
      console.log('Perfil logado:', perfilLogado);
      console.log('Hash empresa:', st_codHashEmpresa);

      if (mounted.current) {
        setUsuariosExcluidos(lista);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      addError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [empresas, perfilLogado, st_codHashEmpresa, addError]);

  // Reativar usuário
  const reativarUsuario = useCallback(async (id) => {
    if (window.confirm("Deseja reativar este usuário?")) {
      try {
        // Busca informações do usuário antes de reativar para o log
        const usuario = usuariosExcluidos.find(u => u.id_usuario === id);
        const nomeUsuario = usuario?.st_nome || 'Usuário desconhecido';
        
        const headers = getHeaders();
        
        await api.patch(`/usuarios/${id}/reativar`, {}, { headers });
        addSuccess(`Usuário "${nomeUsuario}" reativado com sucesso!`);
        await carregarUsuariosExcluidos();
      } catch (error) {
        console.error('Erro ao reativar usuário:', error);
        addError('Erro ao reativar usuário');
      }
    }
  }, [usuariosExcluidos, addSuccess, addError, carregarUsuariosExcluidos]);

  // Alterar senha do usuário
  const alterarSenhaUsuario = useCallback(async (id) => {
    // Busca informações do usuário antes de alterar a senha para o log
    const usuario = usuariosExcluidos.find(u => u.id_usuario === id);
    const nomeUsuario = usuario?.st_nome || 'Usuário desconhecido';
    
    // Perguntar a senha desejada com sugestão
    const novaSenha = prompt(
      `Digite a nova senha para o usuário "${nomeUsuario}":\n\nSugestão: 123Mudar`,
      "123Mudar"
    );
    
    // Se o usuário cancelou ou não digitou nada, não prossegue
    if (!novaSenha || novaSenha.trim() === '') {
      return;
    }
    
    // Confirmação final
    if (window.confirm(`Deseja alterar a senha do usuário "${nomeUsuario}" para "${novaSenha}"?`)) {
      try {
        const headers = getHeaders();
        
        await api.patch(`/usuarios/${id}/senha`, { st_senha: novaSenha }, { headers });
        addSuccess(`Senha do usuário "${nomeUsuario}" alterada com sucesso!`);
      } catch (error) {
        console.error('Erro ao alterar senha:', error);
        addError('Erro ao alterar senha do usuário');
      }
    }
  }, [usuariosExcluidos, addSuccess, addError]);

  // Visualizar usuário
  const visualizarUsuario = useCallback((usuario) => {
    setUsuarioVisualizar(usuario);
    setOpenView(true);
  }, []);

  const fecharVisualizacao = useCallback(() => {
    setOpenView(false);
    setUsuarioVisualizar(null);
  }, []);

  // Handlers
  const handleFiltroChange = useCallback((e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  }, [filtros]);

  const handleRequestSort = useCallback((property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  }, [orderBy, order]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // Memoizar filtros e ordenação
  const usuariosFiltrados = useMemo(() => {
    return usuariosExcluidos
      .filter((usuario) => {
        return (
          usuario.st_nome?.toLowerCase().includes(filtros.st_nome.toLowerCase()) &&
          usuario.st_email?.toLowerCase().includes(filtros.st_email.toLowerCase()) &&
          usuario.en_perfil?.toLowerCase().includes(filtros.en_perfil.toLowerCase()) &&
          (perfilLogado === 'superusuario' ||
            usuario.razao_social?.toLowerCase().includes(filtros.razao_social.toLowerCase()))
        );
      })
      .sort((a, b) => {
        const isAsc = order === "asc";
        if (orderBy === "st_nome" || orderBy === "st_email" || orderBy === "en_perfil" || orderBy === "razao_social") {
          return isAsc
            ? (a[orderBy] || '').localeCompare(b[orderBy] || '')
            : (b[orderBy] || '').localeCompare(a[orderBy] || '');
        }
        return 0;
      });
  }, [usuariosExcluidos, filtros, orderBy, order, perfilLogado]);

  const usuariosPaginados = useMemo(() => {
    return usuariosFiltrados.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [usuariosFiltrados, page, rowsPerPage]);

  // useEffect para carregar dados
  useEffect(() => {
    mounted.current = true;
    carregarEmpresas();
    
    return () => {
      mounted.current = false;
    };
  }, [carregarEmpresas]);

  useEffect(() => {
    if (mounted.current) {
      carregarUsuariosExcluidos();
    }
  }, [carregarUsuariosExcluidos]);

  // Função para retry
  const handleRetry = useCallback(() => {
    setError(null);
    carregarUsuariosExcluidos();
  }, [carregarUsuariosExcluidos]);

  return (
    <AcessoRestrito modulo="recuperarusuario">
      <Box component="main" sx={{ 
        flexGrow: 1,
        p: 0,
        height: '80vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Cabeçalho */}
        <Box sx={{ p: 3, pb: 2 }} display="flex" justifyContent="space-between" flexWrap="wrap">
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => window.history.back()}
              sx={{ 
                minWidth: '120px',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                borderColor: (theme) => theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  borderColor: (theme) => theme.palette.primary.dark
                }
              }}
            >
              Voltar
            </Button>
            <Typography variant="h5" mb={1}>Recuperar Usuários Inativos</Typography>
          </Box>
        </Box>

        {/* Fallback para erros */}
        {error && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert 
              severity="error" 
              action={
                <Button color="inherit" size="small" onClick={handleRetry}>
                  Tentar Novamente
                </Button>
              }
            >
              Erro ao carregar usuários inativos. Verifique sua conexão e tente novamente.
            </Alert>
          </Box>
        )}

        {/* Container da Tabela */}
        {!error && (
          <TableContainer 
            component={Paper} 
            sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'auto',
              height: '100%',
              maxHeight: '100%',
              mx: 3,
              width: 'calc(100% - 48px)',
              mb: 9
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1
                }}
              >
                <CircularProgress />
              </Box>
            )}
            
            <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell width="30%">
                    <TableSortLabel
                      active={orderBy === "st_nome"}
                      direction={orderBy === "st_nome" ? order : "asc"}
                      onClick={() => handleRequestSort("st_nome")}
                    >
                      Nome
                    </TableSortLabel>
                    <TextField
                      name="st_nome"
                      size="small"
                      fullWidth
                      value={filtros.st_nome}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por nome"
                    />
                  </TableCell>
                  <TableCell width="20%">
                    <TableSortLabel
                      active={orderBy === "st_email"}
                      direction={orderBy === "st_email" ? order : "asc"}
                      onClick={() => handleRequestSort("st_email")}
                    >
                      E-mail
                    </TableSortLabel>
                    <TextField
                      name="st_email"
                      size="small"
                      fullWidth
                      value={filtros.st_email}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por email"
                    />
                  </TableCell>
                  <TableCell width="15%">
                    <TableSortLabel
                      active={orderBy === "en_perfil"}
                      direction={orderBy === "en_perfil" ? order : "asc"}
                      onClick={() => handleRequestSort("en_perfil")}
                    >
                      Perfil
                    </TableSortLabel>
                    <TextField
                      name="en_perfil"
                      size="small"
                      fullWidth
                      value={filtros.en_perfil}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por perfil"
                    />
                  </TableCell>
                  {perfilLogado === 'superusuario' && (
                    <TableCell width="20%">
                      <TableSortLabel
                        active={orderBy === "razao_social"}
                        direction={orderBy === "razao_social" ? order : "asc"}
                        onClick={() => handleRequestSort("razao_social")}
                      >
                        Empresa
                      </TableSortLabel>
                      <TextField
                        name="razao_social"
                        size="small"
                        fullWidth
                        value={filtros.razao_social}
                        onChange={handleFiltroChange}
                        placeholder="Filtrar por empresa"
                      />
                    </TableCell>
                  )}
                  <TableCell width="15%">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={perfilLogado === 'superusuario' ? 5 : 4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : usuariosPaginados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={perfilLogado === 'superusuario' ? 5 : 4} align="center">
                      <Typography variant="body1" color="text.secondary">
                        {usuariosExcluidos.length === 0 
                          ? "✅ Nenhum usuário inativo encontrado. Todos os usuários estão ativos no sistema."
                          : "Nenhum usuário corresponde aos filtros aplicados."
                        }
                      </Typography>
                      {usuariosExcluidos.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Para testar este módulo, primeiro desative um usuário no módulo de usuários.
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  usuariosPaginados.map((u) => (
                    <TableRow key={u.id_usuario}>
                      <TableCell>
                        {u.st_nome}
                      </TableCell>
                      <TableCell>
                        {u.st_email}
                      </TableCell>
                      <TableCell>
                        {u.en_perfil}
                      </TableCell>
                      {perfilLogado === 'superusuario' && (
                        <TableCell>
                          {u.razao_social}
                        </TableCell>
                      )}
                      <TableCell>
                        <Tooltip title="Visualizar">
                          <IconButton onClick={() => visualizarUsuario(u)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reativar Usuário">
                          <IconButton 
                            onClick={() => reativarUsuario(u.id_usuario)}
                            color="success"
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Alterar Senha do Usuário">
                          <IconButton 
                            onClick={() => alterarSenhaUsuario(u.id_usuario)}
                            color="warning"
                          >
                            <LockIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={usuariosFiltrados.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1000
          }}
        />

        {/* Dialog de Visualização */}
        <Dialog
          open={openView}
          onClose={fecharVisualizacao}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              width: '700px',
              height: '600px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }}
        >
          {/* Cabeçalho */}
          <DialogTitle
            sx={{
              backgroundColor: (theme) => 
                theme.palette.mode === 'light' ? '#E0F8E0' : '#1B5E20',
              borderBottom: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              fontSize: '1.25rem',
              fontWeight: 500,
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            Visualizar Usuário Inativo
            <IconButton
              onClick={fecharVisualizacao}
              size="small"
              sx={{ color: 'inherit' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          {/* Conteúdo */}
          <DialogContent
            sx={{
              flex: 1,
              padding: '16px',
              overflow: 'auto',
              marginBottom: '60px',
              height: 'calc(100% - 140px)'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Nome"
                value={usuarioVisualizar?.st_nome || ''}
                fullWidth
                InputProps={{
                  readOnly: true
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                    WebkitTextFillColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                  },
                  '& .MuiInputLabel-root': {
                    color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Email"
                  value={usuarioVisualizar?.st_email || ''}
                  sx={{ 
                    width: '50%',
                    '& .MuiInputBase-input': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                      WebkitTextFillColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    },
                    '& .MuiInputLabel-root': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                  InputProps={{
                    readOnly: true
                  }}
                />
                <TextField
                  label="Telefone"
                  value={usuarioVisualizar?.st_telefone || ''}
                  sx={{ 
                    width: '50%',
                    '& .MuiInputBase-input': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                      WebkitTextFillColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    },
                    '& .MuiInputLabel-root': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                  InputProps={{
                    readOnly: true
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Perfil"
                  value={usuarioVisualizar?.en_perfil || ''}
                  sx={{ 
                    width: '50%',
                    '& .MuiInputBase-input': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                      WebkitTextFillColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    },
                    '& .MuiInputLabel-root': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                  InputProps={{
                    readOnly: true
                  }}
                />
                <TextField
                  label="Empresa"
                  value={usuarioVisualizar?.razao_social || ''}
                  sx={{ 
                    width: '50%',
                    '& .MuiInputBase-input': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                      WebkitTextFillColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    },
                    '& .MuiInputLabel-root': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                  InputProps={{
                    readOnly: true
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Data de Criação"
                  value={usuarioVisualizar?.dt_criacao ? new Date(usuarioVisualizar.dt_criacao).toLocaleDateString('pt-BR') : ''}
                  sx={{ 
                    width: '50%',
                    '& .MuiInputBase-input': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                      WebkitTextFillColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    },
                    '& .MuiInputLabel-root': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                  InputProps={{
                    readOnly: true
                  }}
                />
                <TextField
                  label="Data de Exclusão"
                  value={usuarioVisualizar?.ts_deleted ? new Date(usuarioVisualizar.ts_deleted).toLocaleDateString('pt-BR') : ''}
                  sx={{ 
                    width: '50%',
                    '& .MuiInputBase-input': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                      WebkitTextFillColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    },
                    '& .MuiInputLabel-root': {
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                  InputProps={{
                    readOnly: true
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          
          {/* Botões */}
          <DialogActions
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '60px',
              backgroundColor: (theme) => 
                theme.palette.mode === 'light' ? '#E0F8E0' : '#1B5E20',
              borderTop: '1px solid',
              borderColor: 'divider',
              padding: '0 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 1
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {new Date().toLocaleDateString('pt-BR')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                color="success"
                startIcon={<RestoreIcon />}
                onClick={() => {
                  reativarUsuario(usuarioVisualizar?.id_usuario);
                  fecharVisualizacao();
                }}
                sx={{
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  fontWeight: 500
                }}
              >
                REATIVAR
              </Button>
              <Button 
                variant="contained" 
                color="warning"
                startIcon={<LockIcon />}
                onClick={() => {
                  alterarSenhaUsuario(usuarioVisualizar?.id_usuario);
                  fecharVisualizacao();
                }}
                sx={{
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  fontWeight: 500
                }}
              >
                ALTERAR SENHA
              </Button>
              <Button 
                variant="outlined"
                onClick={fecharVisualizacao}
                sx={{
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  fontWeight: 500
                }}
              >
                FECHAR
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    </AcessoRestrito>
  );
}
